import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { LOCAL_SEED } from "../data/localSeed";
import { MODULES } from "../lib/modules";

export function HomeRedirect() {
  const { homePath } = useAuth();
  return <Navigate to={homePath} replace />;
}

export function WorkbenchHome() {
  const { roles, roleSummary, moduleIds, user } = useAuth();
  const primary = MODULES.filter((m) => moduleIds.includes(m.id));
  const isEmployeeish = roles.some(
    (r) => r.id === "ROLE-EMPLOYEE" || r.id === "ROLE-SUPERVISOR",
  );
  const pto = (LOCAL_SEED.leave_balances ?? []).find(
    (b) => String(b.type) === "Accrued PT" || String(b.type) === "Annual",
  );
  const trainingDue = (LOCAL_SEED.training ?? []).filter(
    (t) => String(t.status) === "Overdue" || String(t.status) === "Due soon",
  );
  const metrics = isEmployeeish ? [] : (LOCAL_SEED.bi_metrics ?? []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate mb-1">Workbench</div>
        <h1 className="font-display text-3xl text-navy">
          {roles.length === 1 ? `${roles[0].label} workbench` : "Combined workbench"}
        </h1>
        <p className="text-slate mt-1">
          Welcome{user ? `, ${user.name}` : ""}. Previewing as{" "}
          <strong className="text-navy">{roleSummary}</strong>
          {roles.length > 1 ? " (multiple roles)" : ""}.
        </p>
      </div>

      {isEmployeeish && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <Link to="/app/leave" className="bg-white border border-black/5 p-4 hover:border-teal/40">
            <div className="text-xs uppercase tracking-wider text-slate">Available Accrued PT</div>
            <div className="text-2xl font-semibold text-navy mt-1">
              {pto ? `${pto.balance} ${pto.unit}` : "—"}
            </div>
            <div className="text-xs text-teal mt-1">Open Leave</div>
          </Link>
          <Link
            to="/app/profile"
            className="bg-white border border-black/5 p-4 hover:border-teal/40"
          >
            <div className="text-xs uppercase tracking-wider text-slate">Your people</div>
            <div className="text-sm text-navy mt-2 space-y-1">
              {(LOCAL_SEED.org_contacts ?? []).map((c) => (
                <div key={String(c.id)}>
                  <span className="text-slate">{String(c.role)}:</span> {String(c.name)}
                </div>
              ))}
            </div>
          </Link>
          <Link
            to="/app/profile"
            className="bg-white border border-black/5 p-4 hover:border-teal/40"
          >
            <div className="text-xs uppercase tracking-wider text-slate">Training due</div>
            <div className="text-2xl font-semibold text-navy mt-1">{trainingDue.length}</div>
            <div className="text-xs text-slate mt-1">
              {trainingDue[0] ? String(trainingDue[0].name) : "All clear"}
            </div>
          </Link>
        </div>
      )}

      {metrics.length > 0 && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {metrics.map((m) => (
            <div key={String(m.id)} className="bg-white border border-black/5 p-4">
              <div className="text-xs uppercase tracking-wider text-slate">{String(m.metric)}</div>
              <div className="text-2xl font-semibold text-navy mt-1">{format(m.value)}</div>
              <div className="text-xs text-teal mt-1">{String(m.trend)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-black/5 p-4">
        <h2 className="font-semibold text-navy mb-3">Modules you can open</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {primary.map((m) => (
            <Link
              key={m.id}
              to={m.path}
              className="block border border-black/5 p-3 hover:border-teal/40 hover:bg-teal-soft/40 transition-colors"
            >
              <div className="font-medium text-navy">{m.label}</div>
              <div className="text-sm text-slate mt-1">{m.description}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function format(v: string | number | boolean | null) {
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}
